/**
 * @fileoverview Review & Analysis — overview view
 *
 * Renders one AgentCard per R&A sub-agent. Each entry in `REVIEW_AGENTS`
 * carries a `dataAgentId` pointing at a Convex summarisation manifest, so
 * the card surfaces the same live status (running/completed/failed,
 * duration, document count, categories, concern count) as the Data
 * overview.
 */

import { useNavigate } from "@tanstack/react-router";
import { useQuery, useMutation } from "@tanstack/react-query";
import {
  convexQuery,
  useConvexAction,
  useConvexMutation,
} from "@convex-dev/react-query";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import { toast } from "sonner";
import { Sparkles } from "lucide-react";
import { AgentCard } from "@/components/jobs/phases/gather/summarization/AgentCard";
import { ALL_MANIFEST_UI_METADATA } from "@cvx/jobs/summarization/agents/uiMetadata";
import {
  FLAGGED_HEAD_CONFIGS,
  FLAGGED_MASTER_MANIFEST_ID,
  FLAGGED_UPSTREAM,
} from "@cvx/jobs/summarization/agents/flaggedTransactions/headConfigs";
import {
  OFFENCE_HEAD_CONFIGS,
  OFFENCE_MASTER_MANIFEST_ID,
  OFFENCE_UPSTREAM,
} from "@cvx/jobs/summarization/agents/civilCriminalOffences/headConfigs";
import { reviewAgentsForJob } from "./reviewRegistry";
import { isPersonalInsolvencyAppointmentType } from "@cvx/lib/appointmentTypes";

interface ReviewOverviewProps {
  jobId: string;
  isViewer?: boolean;
}

type TaskRow = {
  _id: Id<"summarizationTasks">;
  summarizationRunId: Id<"summarizationRuns">;
  agentId: string;
  agentName: string;
  categories: string[];
  status: string;
  startedAt?: number;
  completedAt?: number;
  duration?: number;
  documentCount: number;
  documentsAnalysed: number;
  overallAssessment?: string;
  error?: string;
  retryCount?: number;
  practitionerReviewed: boolean;
  concernCount: number;
  latestActivity?: string;
};

/**
 * The shape an AgentCard needs. Real task rows satisfy it directly; the
 * synthesized Flagged Transactions aggregate omits `_id` (it has no single
 * backing task).
 */
type CardTask = {
  _id?: Id<"summarizationTasks">;
  agentId: string;
  agentName: string;
  categories: string[];
  status: string;
  duration?: number;
  documentCount: number;
  documentsAnalysed: number;
  overallAssessment?: string;
  error?: string;
  concernCount: number;
  latestActivity?: string;
};

function buildPlaceholderTask(manifest: {
  id: string;
  name: string;
  triggerCategories: string[];
}): CardTask {
  return {
    agentId: manifest.id,
    agentName: manifest.name,
    categories: manifest.triggerCategories,
    status: "waiting",
    documentCount: 0,
    documentsAnalysed: 0,
    concernCount: 0,
  };
}

// Flagged Transactions runs as eight reactive head agents (sum_flagged_*) plus
// a chat-only master coordinator (sum_flagged_master). The master is never
// document-triggered, so binding the overview card to it makes a fully-run
// analysis read as "Waiting for documents". Aggregate the head tasks instead.
const FLAGGED_HEAD_IDS = new Set(
  Object.values(FLAGGED_HEAD_CONFIGS).map((c) => c.manifestId),
);
const FLAGGED_UPSTREAM_IDS = new Set(Object.keys(FLAGGED_UPSTREAM));

/**
 * Roll the eight head tasks into a single card status. Returns `undefined`
 * when no head has a task row yet (so the card falls back to the proactive
 * "waiting for documents" placeholder).
 */
function aggregateFlaggedTask(
  tasks: ReadonlyArray<TaskRow>,
): CardTask | undefined {
  const heads = tasks.filter((t) => FLAGGED_HEAD_IDS.has(t.agentId));
  if (heads.length === 0) return undefined;

  const anyRunning = heads.some((t) => t.status === "running");
  const anyCompleted = heads.some((t) => t.status === "completed");
  const anyFailed = heads.some((t) => t.status === "failed");
  // Prefer surfacing that work happened: running → completed → failed →
  // waiting. A mix of completed + failed reads as completed (the workspace
  // shows the failed head); all-failed reads as failed.
  const status = anyRunning
    ? "running"
    : anyCompleted
      ? "completed"
      : anyFailed
        ? "failed"
        : "waiting";

  // Documents feeding the analysis come from the upstream data agents (bank
  // transactions, financial analysis, …). Max avoids double-counting the same
  // document across overlapping upstream categories.
  const documentCount = tasks
    .filter((t) => FLAGGED_UPSTREAM_IDS.has(t.agentId))
    .reduce((max, t) => Math.max(max, t.documentCount), 0);

  const startedAts = heads
    .map((t) => t.startedAt)
    .filter((n): n is number => n != null);
  const completedAts = heads
    .map((t) => t.completedAt)
    .filter((n): n is number => n != null);
  const duration =
    status === "completed" && startedAts.length > 0 && completedAts.length > 0
      ? Math.max(...completedAts) - Math.min(...startedAts)
      : undefined;

  return {
    agentId: FLAGGED_MASTER_MANIFEST_ID,
    agentName: "Recoverable Transactions",
    categories: [],
    status,
    duration,
    documentCount,
    documentsAnalysed: 0,
    concernCount: heads.reduce((sum, t) => sum + t.concernCount, 0),
    error:
      status === "failed"
        ? heads.find((t) => t.status === "failed")?.error
        : undefined,
    latestActivity: heads.find((t) => t.status === "running")?.latestActivity,
  };
}

// Civil & Criminal Offences runs as twelve reactive head agents
// (sum_offence_*) plus a chat-only master coordinator (sum_offence_master).
// The master is never document-triggered, so binding the overview card to it
// makes a fully-run analysis read as "Waiting for documents". Aggregate the
// head tasks instead — exactly as Flagged Transactions does.
const OFFENCE_HEAD_IDS = new Set(
  Object.values(OFFENCE_HEAD_CONFIGS).map((c) => c.manifestId),
);
const OFFENCE_UPSTREAM_IDS = new Set(Object.keys(OFFENCE_UPSTREAM));

/**
 * Roll the twelve offence head tasks into a single card status. Returns
 * `undefined` when no head has a task row yet (so the card falls back to the
 * proactive "waiting for documents" placeholder).
 */
function aggregateOffenceTask(
  tasks: ReadonlyArray<TaskRow>,
): CardTask | undefined {
  const heads = tasks.filter((t) => OFFENCE_HEAD_IDS.has(t.agentId));
  if (heads.length === 0) return undefined;

  const anyRunning = heads.some((t) => t.status === "running");
  const anyCompleted = heads.some((t) => t.status === "completed");
  const anyFailed = heads.some((t) => t.status === "failed");
  const status = anyRunning
    ? "running"
    : anyCompleted
      ? "completed"
      : anyFailed
        ? "failed"
        : "waiting";

  // The offence heads are a claim layer — their "documents" come from the
  // upstream review/data agents (flagged heads, Books & Records, Corporate, …).
  // Max avoids double-counting a document across overlapping upstream feeders.
  const documentCount = tasks
    .filter((t) => OFFENCE_UPSTREAM_IDS.has(t.agentId))
    .reduce((max, t) => Math.max(max, t.documentCount), 0);

  const startedAts = heads
    .map((t) => t.startedAt)
    .filter((n): n is number => n != null);
  const completedAts = heads
    .map((t) => t.completedAt)
    .filter((n): n is number => n != null);
  const duration =
    status === "completed" && startedAts.length > 0 && completedAts.length > 0
      ? Math.max(...completedAts) - Math.min(...startedAts)
      : undefined;

  return {
    agentId: OFFENCE_MASTER_MANIFEST_ID,
    agentName: "Civil & Criminal Offences",
    categories: [],
    status,
    duration,
    documentCount,
    documentsAnalysed: 0,
    concernCount: heads.reduce((sum, t) => sum + t.concernCount, 0),
    error:
      status === "failed"
        ? heads.find((t) => t.status === "failed")?.error
        : undefined,
    latestActivity: heads.find((t) => t.status === "running")?.latestActivity,
  };
}

export function ReviewOverview({ jobId, isViewer }: ReviewOverviewProps) {
  const navigate = useNavigate();

  const openAgent = (slug: string) => {
    navigate({
      to: "/dashboard/jobs/$jobId/review/$agent",
      params: { jobId, agent: slug },
    });
  };

  const { data: tasksData } = useQuery(
    convexQuery(
      api.jobs.summarization.publicQueries.getSummarizationTasksByJob,
      { jobId: jobId as Id<"jobs"> },
    ),
  );

  const tasks: ReadonlyArray<TaskRow> = (tasksData ??
    []) as ReadonlyArray<TaskRow>;
  // MOR-1260: the Investigations cards describe the Bankruptcy Act heads on a
  // bankruptcy job instead of the Corporations Act ones.
  const { data: job } = useQuery(
    convexQuery(api.jobs.queries.getJob, { jobId: jobId as Id<"jobs"> }),
  );
  const isBankruptcyJob = isPersonalInsolvencyAppointmentType(
    job?.appointmentType,
  );
  const tasksByAgentId = new Map<string, TaskRow>();
  for (const task of tasks) tasksByAgentId.set(task.agentId, task);

  const { mutateAsync: cancelSummarization } = useMutation({
    mutationFn: useConvexAction(
      api.jobs.summarization.actions.cancelSummarization,
    ),
  });
  const { mutateAsync: rerunAgent } = useMutation({
    mutationFn: useConvexAction(
      api.jobs.summarization.actions.rerunSummarizationAgent,
    ),
  });
  const { mutateAsync: runFlaggedHeads } = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.summarization.agents.flaggedTransactions.publicMutations
        .runHeads,
    ),
  });
  const { mutateAsync: runOffenceHeads } = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.summarization.agents.civilCriminalOffences.publicMutations
        .reRunHeads,
    ),
  });

  const handleRunFlaggedHeads = async () => {
    try {
      await runFlaggedHeads({ jobId: jobId as Id<"jobs"> });
      toast.success("Flagged transactions re-running");
    } catch (e) {
      toast.error(
        e instanceof Error
          ? e.message
          : "Failed to re-run flagged transactions",
      );
    }
  };

  const handleRunOffenceHeads = async () => {
    try {
      await runOffenceHeads({ jobId: jobId as Id<"jobs"> });
      toast.success("Civil & Criminal Offences re-running");
    } catch (e) {
      toast.error(
        e instanceof Error ? e.message : "Failed to re-run offence heads",
      );
    }
  };

  const handleRerunAgent = async (taskId: Id<"summarizationTasks">) => {
    try {
      await rerunAgent({ taskId, jobId: jobId as Id<"jobs"> });
      toast.success("Agent re-running");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to re-run agent");
    }
  };

  const handleCancelAgent = async (taskId: Id<"summarizationTasks">) => {
    try {
      await cancelSummarization({ taskId, jobId: jobId as Id<"jobs"> });
      toast.success("Agent cancelled");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to cancel agent");
    }
  };

  // Resolve each REVIEW_AGENTS entry to its manifest (for triggerCategories,
  // uiDescription, etc.) and current task row. Custom-view entries (Flagged
  // Transactions) are intentionally absent from the Summarisation-tab
  // metadata, so synthesize their card metadata from the registry entry.
  const flaggedAggregate = aggregateFlaggedTask(tasks);
  const offenceAggregate = aggregateOffenceTask(tasks);

  const rows = reviewAgentsForJob(job?.appointmentType)
    .map((entry) => {
      const manifest = ALL_MANIFEST_UI_METADATA.find(
        (m) => m.id === entry.dataAgentId,
      );
      if (!manifest && !entry.customView) return null;
      const isFlagged = entry.dataAgentId === FLAGGED_MASTER_MANIFEST_ID;
      const isOffence = entry.dataAgentId === OFFENCE_MASTER_MANIFEST_ID;
      const baseManifest = manifest ?? {
        id: entry.dataAgentId,
        name: entry.label,
        uiDescription: entry.description,
        triggerCategories: [] as string[],
      };
      return {
        entry,
        manifest:
          isBankruptcyJob && entry.bankruptcyDescription
            ? { ...baseManifest, uiDescription: entry.bankruptcyDescription }
            : baseManifest,
        // Flagged Transactions and Civil & Criminal Offences both aggregate their
        // head tasks (the master each points at is chat-only); every other entry
        // maps 1:1 to its data-agent task.
        task: isFlagged
          ? flaggedAggregate
          : isOffence
            ? offenceAggregate
            : (tasksByAgentId.get(entry.dataAgentId) as CardTask | undefined),
        isFlagged,
        isOffence,
      };
    })
    .filter((r): r is NonNullable<typeof r> => r !== null);

  return (
    // Replicate SummarizationView's bleed-to-edges layout so the page
    // background matches the Data overview exactly.
    <div className="-m-6 flex flex-1 flex-col gap-5 bg-muted px-6 pb-6 pt-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Investigations</h3>
          <div className="mt-1 flex items-center gap-3">
            <span className="flex items-center gap-1 text-xs text-muted-foreground">
              <Sparkles className="h-3 w-3" />
              {rows.length} agents
            </span>
          </div>
        </div>
      </div>

      {/* Single grouped card — mirrors the Data section's group cards. */}
      <div className="overflow-hidden rounded-xl bg-background shadow-[0_1px_3px_rgba(0,0,0,0.08),0_1px_2px_rgba(0,0,0,0.06)] transition-shadow duration-200 hover:shadow-[0_4px_12px_rgba(0,0,0,0.1),0_2px_4px_rgba(0,0,0,0.06)]">
        <div className="flex items-baseline justify-between px-5 py-3">
          <h4 className="text-sm font-semibold">Agents</h4>
        </div>
        <div className="divide-y divide-border">
          {rows.map(({ entry, manifest, task, isFlagged, isOffence }) => {
            const taskId = task?._id;
            return task ? (
              <AgentCard
                key={entry.slug}
                task={{
                  ...task,
                  // Manifest is the source of truth for the display label so
                  // older task rows still show the current name.
                  agentName: entry.label,
                  categories: manifest.triggerCategories,
                }}
                uiDescription={manifest.uiDescription}
                isViewer={isViewer}
                onSelect={() => openAgent(entry.slug)}
                onRerun={
                  isFlagged
                    ? handleRunFlaggedHeads
                    : isOffence
                      ? handleRunOffenceHeads
                      : taskId
                        ? () => handleRerunAgent(taskId)
                        : undefined
                }
                onCancel={
                  isFlagged || isOffence || !taskId
                    ? undefined
                    : () => handleCancelAgent(taskId)
                }
                grouped
              />
            ) : (
              <AgentCard
                key={entry.slug}
                task={buildPlaceholderTask({
                  id: manifest.id,
                  name: entry.label,
                  triggerCategories: manifest.triggerCategories,
                })}
                uiDescription={manifest.uiDescription}
                isViewer={isViewer}
                onSelect={() => openAgent(entry.slug)}
                grouped
              />
            );
          })}
        </div>
      </div>
    </div>
  );
}
